import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import TableCell from '@/components/General/Table/TableCell';
import DropdownMenu from '@/components/General/DropdownMenu';
import { usePressBlockerStore } from '@/data/store/usePressBlockerStore';
import Checkbox from '@/components/General/inputs/Checkbox';
import { Tooltip } from '@/components/General/Tooltip';
import Unicon from '@/components/General/Unicon';
import useTableSelectionStore from '@/data/store/useTableSelectionStore';

const getNestedValue = (obj, path) => {
  if (!path) return '';
  return path.split('.').reduce((p, c) => (p && p[c] !== undefined ? p[c] : null), obj);
};

const TableRow = ({ tableId, item, itemId, columns, lastRow = false, height, onPress, tableWidth, actions = [], batchSelection, onToggleSelect }) => {

  const availableActions = useMemo(() => {
    return actions.filter(action => {
      if (!action.isAvailable) return true;
      return action.isAvailable(item);
    });
  }, [actions, item]);

  const columnCount = columns.length + (availableActions.length > 0 ? 1 : 0);

  const isSelected = useTableSelectionStore(s => batchSelection ? s.isSelected(tableId, itemId) : false);

  const handleToggle = () => {
    onToggleSelect?.(item);
  };

  return (<View style={styles.container(height, onPress, isSelected)}>
      {batchSelection && (
        <View style={styles.checkboxCell}>
          <Checkbox
            testID={`TableRowCheckbox-${tableId}-${itemId}`}
            checked={isSelected}
            onChange={handleToggle}
            size={18}
          />
        </View>
      )}
      <Pressable testID={`TableRow-${tableId}`} disabled={!onPress} onPress={() => {
        if(usePressBlockerStore.getState().isBlocked()) return;
        onPress();
      }} style={styles.innerContainer(height)}>
        {columns.map((column, index) => (
          <TableCell key={index} width={column.width || (tableWidth / columnCount)} minWidth={column.minWidth || (tableWidth / columnCount)} index={index} tableId={tableId}>
            {column.renderer ? column.renderer(item) : getNestedValue(item, column.accessor)}
          </TableCell>
        ))}
      </Pressable>
      
      {availableActions.length > 0 &&
        <TableCell width={100} index={columns.length} tableId={tableId}>
          {availableActions.length <= 3 && availableActions.every(a => a.icon?.web) ? (
            <View style={styles.inlineActions}>
              {availableActions.map((action, idx) => (
                <Tooltip testID={`TableAction-${action.name}`} key={idx} title={action.name} onPress={() => action.onSelect(item)}>
                  <View style={styles.inlineActionButton(action.destructive)}>
                    <Unicon name={action.icon.web} size={20} colorVariant={action.destructive ? 'error' : 'text'} />
                  </View>
                </Tooltip>
              ))}
            </View>
          ) : (
            <DropdownMenu options={availableActions.map(a => ({...a, onSelect: () => a.onSelect(item)}))}/>
          )}
        </TableCell>
      }
      {!lastRow && <View pointerEvents="none" style={styles.separator} />}
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: (height, onPress, isSelected) => ({
    flexDirection: 'row',
    backgroundColor: isSelected ? theme.colors.primaryContainer : 'transparent',
    height: height || 'auto',
    _web: {
      cursor: onPress != null ? 'pointer' : 'default',
      transition: 'all 0.1s ease-in-out',
      _hover: onPress != null ? { backgroundColor: theme.colors.hover } : {}
    }
  }),
  separator: {
    position: 'absolute',
    left: theme.spacing.md,
    right: theme.spacing.md,
    bottom: 0,
    height: 1,
    backgroundColor: theme.colors.borderSubtle,
  },
  innerContainer: (height) => ({
    flexDirection: 'row',
    height: height || 'auto',
    paddingVertical: 5,
  }),
  checkboxCell: {
    width: 50,
    paddingLeft: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  inlineActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  inlineActionButton: (destructive) => ({
    padding: 6,
    borderRadius: 3,
    _web: {
      cursor: 'pointer',
      transition: 'background-color 0.1s ease-in-out',
      _hover: {
        backgroundColor: destructive ? theme.colors.errorContainer : theme.colors.hover,
      },
    },
  }),
}));

export default React.memo(TableRow);
